import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'

// ============================================================================
// Floating Label Input
// ============================================================================

interface FloatingInputProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
	label: string
	error?: boolean
}

const FloatingInput = React.forwardRef<HTMLInputElement, FloatingInputProps>(
	({ className, label, id, error, type = 'text', ...props }, ref) => {
		const generatedId = React.useId()
		const inputId = id || generatedId

		return (
			<div className="floating-input-wrapper">
				<input
					ref={ref}
					type={type}
					id={inputId}
					data-slot="input"
					className={cn(
						'floating-input peer',
						error && 'floating-input-error',
						className,
					)}
					aria-invalid={error || undefined}
					{...props}
					placeholder=" "
				/>
				<label htmlFor={inputId} className="floating-label">
					<span>{label}</span>
				</label>
			</div>
		)
	},
)
FloatingInput.displayName = 'FloatingInput'

// ============================================================================
// Floating Label Textarea
// ============================================================================

interface FloatingTextareaProps
	extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'placeholder'> {
	label: string
	error?: boolean
}

const FloatingTextarea = React.forwardRef<
	HTMLTextAreaElement,
	FloatingTextareaProps
>(({ className, label, id, error, ...props }, ref) => {
	const generatedId = React.useId()
	const inputId = id || generatedId

	return (
		<div className="floating-input-wrapper">
			<textarea
				ref={ref}
				id={inputId}
				data-slot="textarea"
				className={cn(
					'floating-textarea peer',
					error && 'floating-input-error',
					className,
				)}
				aria-invalid={error || undefined}
				{...props}
				placeholder=" "
			/>
			<label htmlFor={inputId} className="floating-label floating-label-textarea">
				<span>{label}</span>
			</label>
		</div>
	)
})
FloatingTextarea.displayName = 'FloatingTextarea'

export { FloatingInput, FloatingTextarea }
